import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Checkbox,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import { motion, useReducedMotion } from "framer-motion";
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import ClientDocumentProfileEditor from '../../../components/ClientDocumentProfileEditor';
import { documentRequest } from "../../../settings/documentSettingsApi";
import {
  DocumentDialog,
  DOC_TYPES,
  Choice,
  CustomerEditor,
  Field,
  SearchPicker,
  Section,
  money,
  nameOf,
} from "./DocumentUI";

export function GenerateDocument({ token, onClose, onDone, repair, canConfigure = false }) {
  const [retryDocument, setRetryDocument] = useState(null);
  const activeRepair = repair || retryDocument;
  const [step, setStep] = useState(0),
    [kind, setKind] = useState(activeRepair?.doc_type || "invoice"),
    [payment, setPayment] = useState(activeRepair?.payment_type || "prepay");
  const [event, setEvent] = useState(null),
    [context, setContext] = useState(null),
    [client, setClient] = useState(null),
    [saved, setSaved] = useState(false),
    [clientMode, setClientMode] = useState("search"),
    [clientRevision, setClientRevision] = useState(0);
  const [preview, setPreview] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [forceNew, setForceNew] = useState(false);
  const reduce = useReducedMotion(),
    eventSeq = useRef(0);
  const scopeRef = useRef(null), alive = useRef(true);
  const scopeKey = JSON.stringify([token, repair?.id || null]);
  if (scopeRef.current?.key !== scopeKey) scopeRef.current = { key: scopeKey };
  useEffect(() => { alive.current = true; return () => { alive.current = false; eventSeq.current += 1; }; }, []);
  const capture = () => { const owner = scopeRef.current; return () => alive.current && scopeRef.current === owner; };
  const pickEvent = async (e) => {
    const current = capture();
    const id = ++eventSeq.current;
    setEvent(e);
    setContext(null);
    setClient(null);
    setSaved(false);
    setBusy(true);
    setError("");
    setPreview(null);
    try {
      const c = await documentRequest(`events/${e.id}/context/`, { token });
      if (!current() || id !== eventSeq.current) return;
      setContext(c);
      setEvent(c);
      const reference = activeRepair?.client ? { id: activeRepair.client } : c.client || (c.candidates?.length === 1 ? c.candidates[0] : null);
      const fullClient = reference?.id ? await documentRequest(`customers/${reference.id}/`, { token }) : null;
      if (!current() || id !== eventSeq.current) return;
      setClient(fullClient);
      setClientRevision(value => value + 1);
      setSaved(!!fullClient && (!!c.client || !!activeRepair?.client));
      setClientMode(fullClient ? "edit" : "search");
    } catch (err) {
      if (current() && id === eventSeq.current) setError(err.message);
    } finally {
      if (current() && id === eventSeq.current) setBusy(false);
    }
  };
  useEffect(() => {
    const current = capture();
    if (activeRepair?.event) pickEvent({ id: activeRepair.event });
    else if (activeRepair?.client)
      documentRequest(`customers/${activeRepair.client}/`, { token })
        .then((c) => {
          if (!current()) return;
          setClient(c);
          setSaved(true);
          setClientMode("edit");
        })
        .catch((e) => { if (current()) setError(e.message); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const payload = () => ({
    doc_type: kind,
    event_id: event?.id,
    client_id: client?.id,
    payment_type: ["invoice", "invoice_contract"].includes(kind)
      ? payment
      : undefined,
    force_new: forceNew,
  });
  const checkReadiness = async () => {
    const current = capture();
    setError(""); setBusy(true);
    try {
      const result = await documentRequest("readiness/", { token, method: "POST", data: payload() });
      if (!current()) return;
      setPreview(result); setStep(2);
    } catch (err) { if (current()) setError(err.message); }
    finally { if (current()) setBusy(false); }
  };
  const next = async () => {
    const current = capture();
    setError("");
    if (step === 0) {
      if (!event && !["contract", "edo_agreement"].includes(kind)) {
        setError("Выберите объект из результатов поиска");
        return;
      }
      setStep(1);
      return;
    }
    if (step === 1) {
      if (!client?.id || !saved) {
        setError("Сохраните реквизиты заказчика и связь с объектом");
        return;
      }
      await checkReadiness();
      return;
    }
    setBusy(true);
    try {
      await documentRequest(activeRepair ? `${activeRepair.id}/regenerate/` : "generate/", {
        token,
        method: "POST",
        data: activeRepair ? {} : payload(),
      });
      if (current()) onDone();
    } catch (e) {
      if (current()) {
        setError(e.message);
        const failed = e.body?.document;
        const id = failed?.id || e.body?.document_id;
        if (id) setRetryDocument(failed || { id, doc_type: kind, client: client?.id, event: event?.id, payment_type: payment });
      }
    } finally {
      if (current()) setBusy(false);
    }
  };
  const pickClient = async (reference) => {
    const current = capture();
    const requestId = ++eventSeq.current;
    setBusy(true); setSaved(false); setError("");
    try {
      // Lookup rows are summaries and can represent a company employee. The
      // detail endpoint resolves the billing owner before any editable fields.
      const fullClient = await documentRequest(`customers/${reference.id}/`, { token });
      if (!current() || requestId !== eventSeq.current) return;
      setClient(fullClient); setClientRevision(value => value + 1); setClientMode("edit");
    } catch (err) { if (current() && requestId === eventSeq.current) setError(err.message); }
    finally { if (current() && requestId === eventSeq.current) setBusy(false); }
  };
  return (
    <DocumentDialog
      wide
      title={activeRepair ? "Исправить и сформировать документ" : "Новый документ"}
      subtitle="Выберите объект, проверьте заказчика и сформируйте файл на сайте."
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button
            variant="plain"
            color="neutral"
            disabled={busy}
            onClick={() => (step ? setStep((s) => s - 1) : onClose())}
          >
            {step ? "Назад" : "Отмена"}
          </Button>
          <Button
            loading={busy}
            disabled={step === 2 && !preview?.ready}
            onClick={next}
          >
            {step === 2
              ? activeRepair
                ? "Повторить формирование"
                : "Сформировать документ"
              : "Продолжить"}
          </Button>
        </>
      }
    >
      <div className="doc-stepper">
        {["Объект и документ", "Заказчик", "Проверка"].map((x, i) => (
          <div key={x} className={`doc-step ${step === i ? "active" : ""}`}>
            <b>{i + 1}</b>
            {x}
          </div>
        ))}
      </div>
      <motion.div
        key={step}
        initial={{ opacity: 0, x: reduce ? 0 : 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: reduce ? 0 : 0.18 }}
      >
        <Stack component="fieldset" disabled={busy} gap={2.5} sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
          {step === 0 && (
            <>
              <div className="doc-grid">
                <Choice
                  label="Тип документа"
                  value={kind}
                  onChange={setKind}
                  options={DOC_TYPES}
                  disabled={!!activeRepair}
                />
                {["invoice", "invoice_contract"].includes(kind) && (
                  <Choice
                    label="Оплата"
                    value={payment}
                    onChange={setPayment}
                    options={[
                      ["prepay", "Предоплата"],
                      ["postpay", "Постоплата"],
                    ]}
                    disabled={!!activeRepair}
                  />
                )}
              </div>
              {activeRepair ? (
                <Card variant="soft">
                  <Typography level="title-sm">
                    {event?.address ||
                      (activeRepair.event
                        ? `Событие №${activeRepair.event}`
                        : "Документ без объекта")}
                  </Typography>
                  <Typography level="body-sm">
                    Повторное формирование сохраняет исходный объект.
                  </Typography>
                </Card>
              ) : (
                <SearchPicker
                  token={token}
                  value={event}
                  onSelect={pickEvent}
                  onClear={() => {
                    ++eventSeq.current;
                    setEvent(null);
                    setContext(null);
                    setClient(null);
                    setSaved(false);
                    setBusy(false);
                  }}
                />
              )}{" "}
              {busy && <CircularProgress size="sm" />}
              {["contract", "edo_agreement"].includes(kind) && (
                <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                  Договор и соглашение можно сформировать на клиента без выбора
                  объекта.
                </Typography>
              )}
              {event && (
                <Alert color={event.client ? "success" : "warning"}>
                  {event.client
                    ? `Заказчик: ${nameOf(event.client)}`
                    : "У объекта пока нет заказчика. На следующем шаге найдите аккаунт или заполните реквизиты."}
                </Alert>
              )}
              {activeRepair && (
                <Typography level="body-xs">
                  Повтор использует номер и дату выбранного документа.
                </Typography>
              )}
            </>
          )}
          {step === 1 && (
            <>
              {activeRepair ? <Alert color="neutral">Заказчик этого документа сохранён: {nameOf(client)}. Можно исправить его реквизиты. Для другого заказчика создайте новый документ.</Alert> : <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  variant={clientMode === "search" ? "solid" : "soft"}
                  onClick={() => setClientMode("search")}
                >
                  Найти в базе
                </Button>
                <Button
                  variant={clientMode === "new" ? "solid" : "soft"}
                  onClick={() => {
                    setClient(null);
                    setSaved(false);
                    setClientMode("new");
                  }}
                >
                  Заполнить нового
                </Button>
                {client && (
                  <Button
                    variant={clientMode === "edit" ? "solid" : "soft"}
                    onClick={() => setClientMode("edit")}
                  >
                    Реквизиты выбранного
                  </Button>
                )}
              </Stack>}
              {context?.client_conflict && (
                <Alert color="danger">{context.client_conflict}</Alert>
              )}
              {clientMode === "search" && (
                <>
                  <SearchPicker
                    kind="clients"
                    token={token}
                    value={client}
                    onSelect={pickClient}
                  />
                  {!!context?.candidates?.length && (
                    <Section
                      title="Совпадения по телефону объекта"
                      description="Выберите аккаунт и проверьте, кто является заказчиком."
                    >
                      {context.candidates.map((c) => (
                        <Button
                          key={c.id}
                          variant="outlined"
                          onClick={() => pickClient(c)}
                        >
                          {nameOf(c)} · {c.phone}
                        </Button>
                      ))}
                    </Section>
                  )}
                </>
              )}
              {clientMode !== "search" && (
                <CustomerEditor
                  key={clientRevision}
                  client={clientMode === "new" ? null : client}
                  event={event}
                  contact={context?.contact}
                  token={token}
                  onDirty={() => setSaved(false)}
                  onSaved={(c) => {
                    setClient(c);
                    setSaved(true);
                    setClientMode("edit");
                  }}
                />
              )}
              {client?.id && <Section title="Договор и документооборот" description="Если действующий договор уже есть, укажите его здесь. Повторно создавать его не требуется.">
                <ClientDocumentProfileEditor clientId={client.id} token={token} compact onSaved={() => setPreview(null)} />
              </Section>}
              {saved && (
                <Alert color="success">
                  Заказчик сохранён. Можно перейти к проверке документа.
                </Alert>
              )}
            </>
          )}
          {step === 2 && preview && (
            <>
              <Card variant="soft" sx={{ borderRadius: "16px" }}>
                <Typography level="title-lg">Перед формированием</Typography>
                {[
                  ["Документ", DOC_TYPES.find(([v]) => v === kind)?.[1]],
                  ["Заказчик", nameOf(preview.client)],
                  ["Объект", preview.address || "Без привязки к объекту"],
                  ["Исполнитель · наша организация", preview.seller || "Не указан"],
                  ["Сумма", money(preview.amount)],
                  ["Шаблон", preview.template],
                ].map(([k, v]) => (
                  <div className="doc-summary-line" key={k}>
                    <span>{k}</span>
                    <strong>{v}</strong>
                  </div>
                ))}
              </Card>
              <div className="doc-grid">
                <Card variant="outlined" sx={{ borderRadius: '14px', minWidth: 0 }}>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>ЗАКАЗЧИК · КЛИЕНТ</Typography>
                  <Typography level="title-sm" sx={{ overflowWrap: 'anywhere' }}>{nameOf(preview.client)}</Typography>
                  {preview.client?.inn && <Typography level="body-sm">ИНН {preview.client.inn}</Typography>}
                  <Button size="sm" variant="plain" onClick={() => setStep(1)}>Реквизиты заказчика</Button>
                </Card>
                <Card variant="outlined" sx={{ borderRadius: '14px', minWidth: 0 }}>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>ИСПОЛНИТЕЛЬ · НАША ОРГАНИЗАЦИЯ</Typography>
                  <Typography level="title-sm" sx={{ overflowWrap: 'anywhere' }}>{preview.seller || 'Не выбрана организация'}</Typography>
                  {preview.seller_details?.inn && <Typography level="body-sm">ИНН {preview.seller_details.inn}</Typography>}
                  {(preview.permissions?.seller_settings ?? canConfigure) ? <Button component="a" href="/documents/settings?tab=seller" target="_blank" rel="noopener noreferrer" size="sm" variant="plain" endDecorator={<OpenInNewRounded />}>Наши реквизиты</Button>
                    : <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Наши реквизиты меняет Администратор или руководитель.</Typography>}
                </Card>
              </div>
              {preview.contract_basis && <Alert color="neutral">Основание: договор № {preview.contract_basis.number} от {preview.contract_basis.date}{preview.contract_basis.source === 'external' ? ' · указан действующий договор' : ' · документ на сайте'}</Alert>}
              {!!preview.items?.length && <Section title="Смета документа" description={preview.pricing?.message || 'Сумма для клиента из выбранного события.'}>
                <Stack gap={0.5}>{preview.items.map((item, index) => <div className="doc-summary-line" key={index}><Box sx={{ minWidth: 0 }}><Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>{item.name}</Typography><Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{item.qty} {item.unit} × {money(item.price)}</Typography></Box><strong>{money(item.sum)}</strong></div>)}</Stack>
                <div className="doc-summary-line"><strong>Итого для клиента</strong><strong>{money(preview.amount)}</strong></div>
              </Section>}
              {(preview.issues?.length ? preview.issues : (preview.errors || []).map(message => ({ message }))).map((issue, index) => (
                <Alert key={`${issue.code || 'error'}:${index}`} color="danger" sx={{ alignItems: 'flex-start' }}>
                  <Stack gap={1} sx={{ minWidth: 0 }}><Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>{issue.message}</Typography>
                    <div className="doc-readiness-actions">
                      {['customer_edit', 'client_document_profile'].includes(issue.action) && <Button size="sm" color="danger" variant="soft" onClick={() => setStep(1)}>{issue.action === 'client_document_profile' ? 'Указать действующий договор' : 'Исправить заказчика'}</Button>}
                      {(preview.permissions?.[issue.action] ?? canConfigure) && ['seller_settings', 'template_settings'].includes(issue.action) && <Button component="a" href={`/documents/settings?tab=${issue.action === 'seller_settings' ? 'seller' : 'templates'}`} target="_blank" rel="noopener noreferrer" size="sm" color="danger" variant="soft" endDecorator={<OpenInNewRounded />}>{issue.action === 'seller_settings' ? 'Открыть наши реквизиты' : 'Открыть шаблоны'}</Button>}
                      {!(preview.permissions?.[issue.action] ?? canConfigure) && ['seller_settings', 'template_settings'].includes(issue.action) && <Typography level="body-xs">Передайте это замечание Администратору или руководителю.</Typography>}
                      {issue.action === 'event_edit' && <Typography level="body-xs">Исправьте смету в карточке события{event ? ` №${event.number || event.deal_id || event.id}` : ''}, затем повторите проверку.</Typography>}
                      {issue.party === 'runtime' && <Typography level="body-xs">Это настройка сервиса формирования, а не реквизиты сторон. Передайте сообщение администратору сервера.</Typography>}
                    </div>
                  </Stack>
                </Alert>
              ))}
              {(preview.warnings || []).map((e) => (
                <Alert key={e} color="warning">
                  {e}
                </Alert>
              ))}
              <Button variant="outlined" color="neutral" startDecorator={<RefreshRounded />} loading={busy} onClick={checkReadiness}>Проверить ещё раз</Button>
              {!activeRepair && (
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Switch
                    checked={forceNew}
                    onChange={(e) => setForceNew(e.target.checked)}
                  />
                  <Typography level="body-sm">
                    Создать новую версию, даже если документ уже есть
                  </Typography>
                </Stack>
              )}
              <Alert color="neutral">
                Формирование создаёт файл только на сайте. Выдача клиенту и
                передача в Диадок выполняются отдельно.
              </Alert>
            </>
          )}
          {error && <Alert color="danger">{error}</Alert>}
        </Stack>
      </motion.div>
    </DocumentDialog>
  );
}

function useDocumentOperationScope(key) {
  const ref = useRef(null), alive = useRef(true);
  if (ref.current?.key !== key) ref.current = { key };
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  return () => { const owner = ref.current; return () => alive.current && ref.current === owner; };
}

export function BundleDocument({ token, onClose, onDone }) {
  const capture = useDocumentOperationScope(token);
  const [failedId, setFailedId] = useState(null);
  const [client, setClient] = useState(null),
    [events, setEvents] = useState([]),
    [payment, setPayment] = useState("postpay"),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const submit = async () => {
    const current = capture();
    setBusy(true);
    setError("");
    try {
      await documentRequest("bundle/", {
        token,
        method: "POST",
        data: {
          client_id: client.id,
          event_ids: events.map((e) => e.id),
          payment_type: payment,
          ...(failedId ? { document_id: failedId } : {}),
        },
      });
      if (current()) onDone();
    } catch (e) {
      if (current()) { setError(e.message); setFailedId(e.body?.document?.id || e.body?.document_id || failedId); }
    } finally {
      if (current()) setBusy(false);
    }
  };
  return (
    <DocumentDialog
      wide
      title="Один счёт на несколько объектов"
      subtitle="Выберите заказчика и добавьте его объекты в общий счёт."
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="plain" color="neutral" disabled={busy} onClick={onClose}>
            Отмена
          </Button>
          <Button
            disabled={!client || !events.length}
            loading={busy}
            onClick={submit}
          >
            Сформировать ·{" "}
            {money(events.reduce((s, e) => s + Number(e.amount || 0), 0))}
          </Button>
        </>
      }
    >
      <Stack component="fieldset" disabled={busy || !!failedId} gap={2.5} sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
        <SearchPicker
          kind="clients"
          token={token}
          value={client}
          onSelect={(c) => {
            setClient(c);
            setEvents([]);
          }}
        />
        {client && (
          <SearchPicker
            token={token}
            clientId={client.id}
            exclude={events.map((e) => e.id)}
            label="Добавить объект заказчика"
            onSelect={(e) => setEvents((v) => [...v, e])}
          />
        )}
        {events.map((e) => (
          <Card key={e.id}>
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Box>
                <Typography level="title-sm">{e.address}</Typography>
                <Typography level="body-xs">
                  Сделка №{e.deal_id} · {money(e.amount)}
                </Typography>
              </Box>
              <Button
                variant="plain"
                color="danger"
                size="sm"
                onClick={() => setEvents((v) => v.filter((x) => x.id !== e.id))}
              >
                Убрать
              </Button>
            </Stack>
          </Card>
        ))}
        <Choice
          label="Тип оплаты"
          value={payment}
          onChange={setPayment}
          options={[
            ["prepay", "Предоплата"],
            ["postpay", "Постоплата"],
          ]}
        />
        {error && <Alert color="danger">{error}</Alert>}
      </Stack>
    </DocumentDialog>
  );
}
export function DeliverDocument({ token, doc, onClose, onDone, onPartial }) {
  const capture = useDocumentOperationScope(JSON.stringify([token, doc.id]));
  const [partial, setPartial] = useState(false), [emailRetryConfirmed, setEmailRetryConfirmed] = useState(false);
  const [method, setMethod] = useState("lk"),
    [email, setEmail] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const submit = async () => {
    const current = capture();
    setBusy(true);
    setError("");
    try {
      await documentRequest(`${doc.id}/deliver/`, {
        token,
        method: "POST",
        data: { method, email, ...(emailRetryConfirmed ? { email_retry_confirmed: true } : {}) },
      });
      if (current()) onDone();
    } catch (e) {
      if (current()) {
        setError(e.message);
        if (e.body?.partial_success && e.body?.published_to_client) {
          setPartial(true); setEmailRetryConfirmed(false);
          onPartial?.(e.body.document);
        }
      }
    } finally {
      if (current()) setBusy(false);
    }
  };
  return (
    <DocumentDialog
      title="Выдать документ"
      subtitle={`${doc.doc_type_display} № ${doc.number}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="plain" color="neutral" disabled={busy} onClick={onClose}>
            Отмена
          </Button>
          <Button loading={busy} disabled={partial && method === 'email' && !emailRetryConfirmed} onClick={submit}>
            {method === "edo"
              ? "Выдать и поставить в очередь ЭДО"
              : "Выдать документ"}
          </Button>
        </>
      }
    >
      <Stack component="fieldset" disabled={busy} gap={2} sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
        <Choice
          label="Способ выдачи"
          value={method}
          onChange={setMethod}
          options={[
            ["lk", "Личный кабинет клиента"],
            ["email", "Личный кабинет и письмо с PDF"],
            ["manager", "Передать менеджеру"],
            ["edo", "Кабинет клиента и черновик Диадока"],
          ]}
        />
        {method === "email" && (
          <Field
            label="Email получателя"
            type="email"
            value={email}
            onChange={value => { setEmail(value); setEmailRetryConfirmed(false); }}
          />
        )}
        {method === "edo" && (
          <Alert color="warning">
            Будет опубликован документ в кабинете клиента и создано задание
            передачи в Диадок. Руководитель отдельно подписывает и отправляет
            черновик.
          </Alert>
        )}
        {partial && method === 'email' && <Checkbox checked={emailRetryConfirmed} onChange={e => setEmailRetryConfirmed(e.target.checked)} label="Я проверил почту получателя и подтверждаю повторную отправку письма" />}
        {partial && <Alert color="warning">Документ уже опубликован в кабинете клиента. Отправка выбранным способом не подтверждена; проверьте ошибку ниже. Повторная попытка не создаёт новый документ.</Alert>}
        {error && <Alert color="danger">{error}</Alert>}
      </Stack>
    </DocumentDialog>
  );
}
